import { useButton } from '@react-aria/button';
import { FocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';
import type { FocusableRef } from '@react-types/shared';
import classNames from 'classnames';
import React, { forwardRef } from 'react';
import type { ActionButtonProps } from '../../../interface-and-type';
import { useFocusableRef } from '../../../utils';
import { Text } from '../../date-picker-text';

interface IProps extends ActionButtonProps {
  btnClassName: string;
}

const ActionButton = (props: IProps, ref: FocusableRef<HTMLButtonElement>) => {
  let { isQuiet, isDisabled, staticColor, children, autoFocus, btnClassName } = props;

  let domRef = useFocusableRef(ref);
  let { buttonProps, isPressed } = useButton(props, domRef);
  let { hoverProps, isHovered } = useHover({ isDisabled });
  let isTextOnly = React.Children.toArray(props.children).every((c) => !React.isValidElement(c));

  return (
    <FocusRing focusRingClass={classNames('focus-ring-class', {})} autoFocus={autoFocus}>
      <button
        style={props.UNSAFE_style}
        {...mergeProps(buttonProps, hoverProps)}
        ref={domRef}
        className={classNames(
          `btn-class p-4 ${btnClassName}`,
          {
            'quiet-class': isQuiet,
            'staticColor-class': !!staticColor,
            'staticWhite-class': staticColor === 'white',
            'staticBlack-class': staticColor === 'black',
            'is-active-class': isPressed,
            'is-disabled-class': isDisabled,
            'is-hovered-class': isHovered
          },
          props.UNSAFE_className
        )}
      >
        {typeof children === 'string' || isTextOnly ? <Text>{children}</Text> : children}
      </button>
    </FocusRing>
  );
};

let _ActionButton = forwardRef(ActionButton);
export { _ActionButton as ActionButton };
